import { useState, useEffect } from 'react'
import { useNavigate, useLocation, Link } from 'react-router-dom'
import { useAuth } from '../hooks/useAuth'
import { contributionAPI, adminAPI, repositoryAPI, userAPI, authAPI } from '../services/api'
import { EMAIL_SPAM_NOTE } from '../config/support'
import { enrichContributionsWithRepositorySlugs } from '../utils/enrichContributionsWithRepositorySlugs'
import { getDeviceTypeLabel } from '../utils/deviceType'
import FeaturedRepositories from '../components/FeaturedRepositories'
import ContributionForm from '../components/ContributionForm'
import ContributionCard from '../components/ContributionCard'
import CertifiedBadge from '../components/CertifiedBadge'
import ManageRepositoryModal from '../components/ManageRepositoryModal'
import './Page.css'
import './Dashboard.css'

function ContributorDashboard() {
  const { user, refreshUser } = useAuth()
  const navigate = useNavigate()
  const location = useLocation()
  const [showForm, setShowForm] = useState(false)
  const [editingContributionId, setEditingContributionId] = useState(null)
  const [contributions, setContributions] = useState([])
  const [isLoading, setIsLoading] = useState(true)
  const [isProcessing, setIsProcessing] = useState(false)
  const [selectedContribution, setSelectedContribution] = useState(null)
  const [showRejectModal, setShowRejectModal] = useState(false)
  const [rejectReason, setRejectReason] = useState('')
  const [stats, setStats] = useState({
    total: 0,
    draft: 0,
    pending: 0,
    approved: 0,
    rejected: 0,
  })
  const [showApprovedMessage, setShowApprovedMessage] = useState({}) // Track which contributions show the approved message
  const [repositorySlugs, setRepositorySlugs] = useState({}) // Cache repository slugs by ID
  const [bookmarks, setBookmarks] = useState([])
  const [bookmarksLoading, setBookmarksLoading] = useState(false)
  const [submissionMode, setSubmissionMode] = useState('contribute') // 'contribute' | 'new_repository'
  const [pendingRepoApprovals, setPendingRepoApprovals] = useState([])
  const [pendingRepoApprovalsLoading, setPendingRepoApprovalsLoading] = useState(false)
  const [pendingRepoApprovalsError, setPendingRepoApprovalsError] = useState(null)
  const [myRepos, setMyRepos] = useState([])
  const [myReposLoading, setMyReposLoading] = useState(false)
  const [managingRepo, setManagingRepo] = useState(null)
  const [deletingRepoSlug, setDeletingRepoSlug] = useState(null)
  const [verifyResendMsg, setVerifyResendMsg] = useState(null)

  const handleResendVerification = async () => {
    setVerifyResendMsg(null)
    try {
      const r = await authAPI.resendVerification()
      setVerifyResendMsg(r?.message || `Verification email sent. ${EMAIL_SPAM_NOTE}`)
      await refreshUser()
    } catch (e) {
      setVerifyResendMsg(e.message || 'Could not send email')
    }
  }

  useEffect(() => {
    loadContributions()
    loadBookmarks()
    loadPendingRepoApprovals()
    loadMyRepositories()
  }, [])

  // Open edit form when navigated from DashboardSubmissions with edit state
  useEffect(() => {
    const editId = location.state?.editContributionId
    if (editId) {
      setEditingContributionId(editId)
      setShowForm(true)
      navigate(location.pathname, { replace: true, state: {} })
    }
  }, [location.state?.editContributionId, location.pathname, navigate])

  // Open new repository form directly when navigated with state
  useEffect(() => {
    if (location.state?.openNewRepository) {
      setSubmissionMode('new_repository')
      setEditingContributionId(null)
      setShowForm(true)
      navigate(location.pathname, { replace: true, state: {} })
    }
  }, [location.state?.openNewRepository, location.pathname, navigate])

  const loadContributions = async () => {
    try {
      setIsLoading(true)
      const response = await contributionAPI.list({ page_size: 50 })
      let contributions = response.contributions || []
      try {
        const { contributions: enriched, slugByRepoId } = await enrichContributionsWithRepositorySlugs(
          contributions,
          (params) => repositoryAPI.list(params)
        )
        contributions = enriched
        setRepositorySlugs((prev) => {
          const next = { ...prev, ...slugByRepoId }
          for (const c of contributions) {
            if (c.repository_id && c.repository_slug) {
              next[c.repository_id] = c.repository_slug
            }
          }
          return next
        })
      } catch (slugErr) {
        console.error('Failed to resolve repository slugs:', slugErr)
      }
      setContributions(contributions)
      
      // Calculate stats
      const newStats = {
        total: response.total || 0,
        draft: 0,
        pending: 0,
        approved: 0,
        rejected: 0,
      }
      
      response.contributions?.forEach(contrib => {
        if (contrib.status in newStats) {
          newStats[contrib.status]++
        }
      })
      
      setStats(newStats)
    } catch (error) {
      console.error('Failed to load contributions:', error)
      // Don't block the UI if contributions fail to load
      // User can still create new contributions
      setContributions([])
      setStats({ total: 0, draft: 0, pending: 0, approved: 0, rejected: 0 })
    } finally {
      setIsLoading(false)
    }
  }

  const loadBookmarks = async () => {
    try {
      setBookmarksLoading(true)
      const response = await userAPI.getBookmarks(1, 20)
      setBookmarks(response.repositories || [])
    } catch (error) {
      console.error('Failed to load bookmarks:', error)
      setBookmarks([])
    } finally {
      setBookmarksLoading(false)
    }
  }

  const loadPendingRepoApprovals = async () => {
    try {
      setPendingRepoApprovalsError(null)
      setPendingRepoApprovalsLoading(true)
      const response = await contributionAPI.getPendingForOwnedRepos({ page_size: 50 })
      setPendingRepoApprovals(response.contributions || [])
    } catch (error) {
      console.error('Failed to load pending repo approvals:', error)
      setPendingRepoApprovals([])
      setPendingRepoApprovalsError(error?.message || 'Failed to load approval requests')
    } finally {
      setPendingRepoApprovalsLoading(false)
    }
  }

  const loadMyRepositories = async () => {
    try {
      setMyReposLoading(true)
      const response = await userAPI.getMyRepositories(1, 100)
      setMyRepos(response.repositories || [])
    } catch (error) {
      console.error('Failed to load my repositories:', error)
      setMyRepos([])
    } finally {
      setMyReposLoading(false)
    }
  }

  const handleStartContribution = () => {
    setSubmissionMode('contribute')
    setEditingContributionId(null)
    setShowForm(true)
  }

  const handleCreateNewRepository = () => {
    setSubmissionMode('new_repository')
    setEditingContributionId(null)
    setShowForm(true)
  }

  const handleDeleteRepository = async (repo) => {
    const label = repo.title || repo.slug || 'this repository'
    const confirmed = window.confirm(
      `Delete "${label}"?\n\nIt will be removed from browse and your dashboard. This uses the same hide as admin suspend (not a permanent wipe). Contact support if you need it restored.`,
    )
    if (!confirmed || !repo.slug) {
      return
    }

    setDeletingRepoSlug(repo.slug)
    try {
      await repositoryAPI.deleteOwn(repo.slug)
      setMyRepos((current) => current.filter((r) => r.slug !== repo.slug))
    } catch (error) {
      alert(error.message || 'Failed to delete repository')
    } finally {
      setDeletingRepoSlug(null)
    }
  }

  const handleEditContribution = (contributionId) => {
    setEditingContributionId(contributionId)
    setShowForm(true)
  }

  const handleFormSuccess = () => {
    setShowForm(false)
    setEditingContributionId(null)
    loadContributions()
  }

  const handleFormCancel = () => {
    setShowForm(false)
    setEditingContributionId(null)
  }

  const handleApprove = async (contributionId) => {
    if (!window.confirm('Are you sure you want to approve this contribution?')) {
      return
    }

    try {
      setIsProcessing(true)
      // Check if user is admin - if not, use contribution API directly
      const isAdmin = user?.role === 'admin'
      
      if (isAdmin) {
        // Try admin API first
        try {
          await adminAPI.approveContribution(contributionId)
        } catch (adminError) {
          // If admin API fails, try contribution API as fallback
          console.log('Admin API failed, trying contribution API:', adminError)
          await contributionAPI.approve(contributionId)
        }
      } else {
        // Non-admin users use contribution API directly
        await contributionAPI.approve(contributionId)
      }
      
      await loadContributions()
      await loadPendingRepoApprovals()
      
      // Show success message for this contribution, auto-dismiss after 5 seconds
      setShowApprovedMessage(prev => ({ ...prev, [contributionId]: true }))
      setTimeout(() => {
        setShowApprovedMessage(prev => {
          const newState = { ...prev }
          delete newState[contributionId]
          return newState
        })
      }, 5000)
    } catch (error) {
      console.error('Approval error:', error)
      alert(error.message || 'Failed to approve contribution')
    } finally {
      setIsProcessing(false)
    }
  }

  const handleRejectClick = (contribution) => {
    setSelectedContribution(contribution)
    setRejectReason('')
    setShowRejectModal(true)
  }

  const handleRejectSubmit = async () => {
    if (!rejectReason.trim()) {
      alert('Please provide a rejection reason')
      return
    }

    try {
      setIsProcessing(true)
      await contributionAPI.reject(selectedContribution.id, rejectReason)
      setShowRejectModal(false)
      setSelectedContribution(null)
      setRejectReason('')
      await loadContributions()
      await loadPendingRepoApprovals()
    } catch (error) {
      alert(error.message || 'Failed to reject contribution')
    } finally {
      setIsProcessing(false)
    }
  }

  const getStatusBadgeClass = (status) => {
    const classes = {
      draft: 'contribution-status-draft',
      pending: 'contribution-status-pending',
      approved: 'contribution-status-approved',
      rejected: 'contribution-status-rejected',
    }
    return classes[status] || 'contribution-status-default'
  }

  const formatDate = (dateString) => {
    if (!dateString) return ''
    const date = new Date(dateString)
    return date.toLocaleDateString('en-US', { 
      year: 'numeric', 
      month: 'short', 
      day: 'numeric' 
    })
  }

  // Get repository slug for a contribution (check cache or use from contribution)
  const getRepositorySlug = (contrib) => {
    if (contrib.repository_slug) {
      return contrib.repository_slug
    }
    if (contrib.repository_id && repositorySlugs[contrib.repository_id]) {
      return repositorySlugs[contrib.repository_id]
    }
    return null
  }

  const getSubmissionTypeLabel = (type) => {
    const labels = {
      new_repository: 'New repository',
      new_commit: 'New commit',
      new_issue: 'New issue',
      circuit_notes: 'Circuit notes',
    }
    return labels[type] || type
  }

  // Merge contributions and forks into one activity list (newest first)
  const forkRepos = myRepos.filter((r) => r.parent_repository_id)
  const managedRepositoryIds = new Set(myRepos.map((repo) => repo.id))
  const activityItems = [
    ...contributions.map((c) => ({
      type: 'contribution',
      date: c.submitted_at || c.created_at,
      contrib: c,
    })),
    ...forkRepos.map((r) => ({
      type: 'fork',
      date: r.created_at,
      repo: r,
    })),
  ].sort((a, b) => new Date(b.date) - new Date(a.date))
  const activityPreviewCount = 6
  const activityPreview = activityItems.slice(0, activityPreviewCount)
  const hasMoreActivity = activityItems.length > activityPreviewCount

  if (showForm) {
    return (
      <div className="page-container">
        <ContributionForm
          contributionId={editingContributionId}
          mode={editingContributionId ? undefined : submissionMode}
          onSuccess={handleFormSuccess}
          onCancel={handleFormCancel}
        />
      </div>
    )
  }

  const hasPendingContributions = contributions.some(c => c.status === 'pending')
  
  const statsBar = (
    <div className="dashboard-stats-bar">
      <div className="stat-item">
        <div className="stat-number">{stats.total}</div>
        <div className="stat-label">Total</div>
      </div>
      <div className="stat-item">
        <div className="stat-number">{stats.draft}</div>
        <div className="stat-label">Draft</div>
      </div>
      <div className="stat-item">
        <div className="stat-number">{stats.pending}</div>
        <div className="stat-label">Pending</div>
      </div>
      <div className="stat-item">
        <div className="stat-number">{stats.approved}</div>
        <div className="stat-label">Approved</div>
      </div>
      <div className="stat-item">
        <div className="stat-number">{stats.rejected}</div>
        <div className="stat-label">Rejected</div>
      </div>
    </div>
  )

  return (
    <div className="page-container">
      <div className="dashboard-header">
        <h1>
          Welcome back
          {user?.username ? (
            <>
              , {user.username}
              <CertifiedBadge user={user} />
            </>
          ) : ''}
        </h1>
        <p className="dashboard-subtitle">Your contributor dashboard</p>
        {statsBar}
        {user?.role === 'admin' && hasPendingContributions && (
          <div className="dashboard-admin-banner">
            <div>
              <strong>Admin:</strong> You have pending contributions waiting for review.
            </div>
            <button
              type="button"
              className="btn btn-primary"
              onClick={() => navigate('/admin')}
            >
              Go to Admin Dashboard
            </button>
          </div>
        )}
        {user && user.email_verified === false && (
          <div className="dashboard-admin-banner" style={{ borderColor: 'var(--accent)' }}>
            <div>
              <strong>Verify your email</strong> to submit contributions, upload files, bookmark repos, and follow users.
              {' '}{EMAIL_SPAM_NOTE}
              {verifyResendMsg && (
                <span style={{ display: 'block', marginTop: '0.35rem', fontWeight: 'normal', fontSize: '0.9rem' }}>
                  {verifyResendMsg}
                </span>
              )}
            </div>
            <button type="button" className="btn btn-secondary" onClick={handleResendVerification}>
              Resend verification email
            </button>
          </div>
        )}
      </div>

      {/* Repositories you manage (above submissions and pull requests) */}
      <div className="dashboard-card" style={{ marginBottom: '1.5rem' }}>
        <h3>Repositories you manage</h3>
        {myReposLoading ? (
          <p className="empty-state" style={{ margin: 0 }}>Loading...</p>
        ) : myRepos.length === 0 ? (
          <p className="empty-state" style={{ margin: 0 }}>You don&apos;t manage any repositories yet. Create one or fork an existing repo.</p>
        ) : (
          <div className="repos-you-manage-grid">
            {myRepos.map((repo) => (
              <div key={repo.id} className="repos-you-manage-card">
                <div className="repos-you-manage-card-title">{repo.title}</div>
                {repo.slug && (
                  <div className="repos-you-manage-card-meta">{repo.slug}</div>
                )}
                <div className="repos-you-manage-card-badges">
                    {repo.managed_as === 'co_owner' ? (
                      <span className="repos-you-manage-card-badge repos-you-manage-card-badge-co-owner">Co-owner</span>
                    ) : (
                      <span className="repos-you-manage-card-badge repos-you-manage-card-badge-owner">Owner</span>
                    )}
                    {repo.parent_repository_id && (
                      <span className="repos-you-manage-card-badge">Fork</span>
                    )}
                    {repo.visibility && repo.visibility !== 'public' && (
                      <span className="repos-you-manage-card-badge">{repo.visibility}</span>
                    )}
                </div>
                <div className="repos-you-manage-card-actions">
                  <button
                    type="button"
                    className="btn btn-primary"
                    onClick={() => navigate(`/repositories/${repo.slug}`)}
                  >
                    View repository →
                  </button>
                  <button
                    type="button"
                    className="btn btn-secondary"
                    onClick={() => setManagingRepo(repo)}
                  >
                    Manage
                  </button>
                  <button
                    type="button"
                    className="btn btn-secondary repos-you-manage-delete-btn"
                    disabled={deletingRepoSlug === repo.slug}
                    onClick={() => handleDeleteRepository(repo)}
                  >
                    {deletingRepoSlug === repo.slug ? 'Deleting…' : 'Delete'}
                  </button>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      <div className="dashboard-grid">
        <div className="dashboard-card">
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '1rem', flexWrap: 'wrap', gap: '0.5rem' }}>
            <h3>Your Activity</h3>
            <div style={{ display: 'flex', gap: '0.5rem', flexWrap: 'wrap' }}>
              <button className="btn btn-primary" onClick={handleStartContribution}>
                Contribute to existing repo
              </button>
              <button className="btn btn-secondary" onClick={handleCreateNewRepository}>
                Create new repository
              </button>
            </div>
          </div>
          
          {(isLoading && activityItems.length === 0) ? (
            <div className="empty-state">
              <p>Loading...</p>
            </div>
          ) : activityItems.length === 0 ? (
            <div className="empty-state">
              <p>No activity yet.</p>
            </div>
          ) : (
            <>
              <div className="contributions-list contributions-list-preview">
                {activityPreview.map((item) =>
                  item.type === 'fork' ? (
                    <div
                      key={`fork-${item.repo.id}`}
                      className="contribution-item"
                      style={{ cursor: 'pointer', padding: '0.75rem', border: '1px solid var(--border)', borderRadius: '8px', marginBottom: '0.5rem' }}
                      onClick={() => navigate(`/repositories/${item.repo.slug}`)}
                      onKeyDown={(e) => e.key === 'Enter' && navigate(`/repositories/${item.repo.slug}`)}
                      role="button"
                      tabIndex={0}
                    >
                      <div className="contribution-item-header">
                        <span className="contribution-item-title">
                          Forked{' '}
                          <span style={{ fontWeight: 600, color: 'var(--accent)' }}>{item.repo.title}</span>
                          {item.repo.parent_repository_slug && (
                            <>
                              {' '}from{' '}
                              <Link to={`/repositories/${item.repo.parent_repository_slug}`} onClick={(e) => e.stopPropagation()} style={{ fontWeight: 600, color: 'var(--accent)' }}>
                                {item.repo.parent_repository_title || item.repo.parent_repository_slug}
                              </Link>
                            </>
                          )}
                        </span>
                        <span className="contribution-status-badge" style={{ backgroundColor: 'var(--bg-tertiary)', color: 'var(--text-muted)', border: '1px solid var(--border)' }}>Fork</span>
                      </div>
                      <div className="contribution-item-meta">
                        <span>{formatDate(item.date)}</span>
                      </div>
                    </div>
                  ) : (
                    <ContributionCard
                      key={item.contrib.id}
                      contrib={item.contrib}
                      repoSlug={getRepositorySlug(item.contrib)}
                      user={user}
                      showApprovedMessage={showApprovedMessage}
                      formatDate={formatDate}
                      getSubmissionTypeLabel={getSubmissionTypeLabel}
                      getStatusBadgeClass={getStatusBadgeClass}
                      onEdit={handleEditContribution}
                      onApprove={handleApprove}
                      onReject={handleRejectClick}
                      onCardClick={(slug) => navigate(`/repositories/${slug}`)}
                      isProcessing={isProcessing}
                      managedRepositoryIds={managedRepositoryIds}
                    />
                  )
                )}
              </div>
              {hasMoreActivity && (
                <div style={{ marginTop: '1rem', textAlign: 'center' }}>
                  <Link to="/dashboard/submissions" className="btn btn-secondary">
                    See all activity ({activityItems.length})
                  </Link>
                </div>
              )}
            </>
          )}
        </div>

        <div className="dashboard-card">
          <h3>Pull requests</h3>
          {pendingRepoApprovalsLoading ? (
            <div className="empty-state">
              <p>Loading...</p>
            </div>
          ) : pendingRepoApprovalsError ? (
            <div className="repo-approvals-placeholder">
              <p style={{ color: 'var(--error, #dc3545)', marginBottom: '0.75rem' }}>
                {pendingRepoApprovalsError}
              </p>
              {typeof pendingRepoApprovalsError === 'string' &&
                pendingRepoApprovalsError.toLowerCase().includes('verify your email') &&
                user?.id && (
                  <p className="repo-approvals-placeholder-hint" style={{ marginBottom: '1rem' }}>
                    Check your inbox for the verification link, including spam or junk, or resend one from{' '}
                    <Link to={`/users/${user.id}`}>your profile</Link>.
                  </p>
                )}
              <button className="btn btn-primary" onClick={loadPendingRepoApprovals}>
                Retry
              </button>
            </div>
          ) : pendingRepoApprovals.length === 0 ? (
            <div className="repo-approvals-placeholder">
              <p>No pending requests.</p>
              <p className="repo-approvals-placeholder-hint">
                When someone submits a commit or circuit notes to a <strong>repository you manage</strong>, their changes go to a branch and a pull request is opened. Use <strong>Merge into main</strong> to merge their branch with main and approve the contribution. Issues appear here for approval too.
              </p>
              <p className="repo-approvals-placeholder-hint" style={{ marginTop: '0.5rem' }}>
                You need to own at least one repository (e.g. create one via “Create new repository”) and have another user submit a contribution to it.
              </p>
              <button className="btn btn-secondary" onClick={loadPendingRepoApprovals} style={{ marginTop: '1rem' }}>
                Refresh
              </button>
            </div>
          ) : (
            <div className="contributions-list">
              {pendingRepoApprovals.map((contrib) => (
                <div key={contrib.id} className="contribution-item">
                  <div className="contribution-item-header">
                    <div className="contribution-item-info">
                      <span className="contribution-item-title">
                        Contribution to{' '}
                        {contrib.repository_slug ? (
                          <Link to={`/repositories/${contrib.repository_slug}`} style={{ color: 'var(--accent)', fontWeight: 600 }}>
                            {contrib.repository_slug}
                          </Link>
                        ) : (
                          <strong>repository</strong>
                        )}
                        <span className="contribution-item-type"> · {getSubmissionTypeLabel(contrib.submission_type)}</span>
                      </span>
                    </div>
                    <span className="contribution-status-badge contribution-status-pending">pending</span>
                  </div>
                  <div className="contribution-item-meta">
                    {contrib.contributor?.username && (
                      <span>
                        By {contrib.contributor.username}
                        <CertifiedBadge user={contrib.contributor} />
                      </span>
                    )}
                    <span>Created: {formatDate(contrib.created_at)}</span>
                    {contrib.submitted_at && (
                      <span>Submitted: {formatDate(contrib.submitted_at)}</span>
                    )}
                  </div>
                  {contrib.submission_type === 'new_issue' && (contrib.metadata?.notes || contrib.metadata?.title) && (
                    <div className="contribution-issue-preview" style={{ marginTop: '0.75rem', padding: '0.75rem', background: 'var(--bg-tertiary)', borderRadius: '6px', border: '1px solid var(--border)' }}>
                      {contrib.metadata?.title && (
                        <div style={{ fontWeight: 600, marginBottom: '0.25rem' }}>{contrib.metadata.title}</div>
                      )}
                      <div style={{ whiteSpace: 'pre-wrap', wordBreak: 'break-word', fontSize: '0.95rem', color: 'var(--text-secondary)' }}>
                        {(contrib.metadata?.notes || '').slice(0, 500)}{(contrib.metadata?.notes && contrib.metadata.notes.length > 500) ? '…' : ''}
                      </div>
                    </div>
                  )}
                  <div style={{ marginTop: '0.75rem', display: 'flex', flexWrap: 'wrap', gap: '0.5rem', alignItems: 'center' }}>
                    {contrib.pull_request_id && contrib.repository_slug && (
                      <Link
                        to={`/repositories/${contrib.repository_slug}?branch=contrib/${contrib.id.slice(0, 8)}`}
                        className="btn btn-secondary"
                        style={{ flex: '0 0 auto' }}
                      >
                        View branch
                      </Link>
                    )}
                    <button
                      className={contrib.pull_request_id ? 'btn btn-merge' : 'btn btn-primary'}
                      onClick={() => handleApprove(contrib.id)}
                      disabled={isProcessing}
                      style={{ flex: 1, minWidth: '120px' }}
                    >
                      {contrib.pull_request_id ? 'Merge into main' : 'Approve'}
                    </button>
                    <button
                      className="btn btn-secondary"
                      onClick={() => handleRejectClick(contrib)}
                      disabled={isProcessing}
                      style={{ backgroundColor: '#dc3545', color: 'white', borderColor: '#dc3545', flex: 1, minWidth: '80px' }}
                    >
                      Reject
                    </button>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>

      {/* Bookmarks Section */}
      <div style={{ marginTop: '2rem' }}>
        <div className="dashboard-card">
          <h3>Bookmarks</h3>
          {bookmarksLoading ? (
            <div className="empty-state">
              <p>Loading bookmarks...</p>
            </div>
          ) : bookmarks.length === 0 ? (
            <div className="empty-state">
              <p>No bookmarked repositories yet. Bookmark repositories to save them for later.</p>
            </div>
          ) : (
            <div style={{ display: 'flex', flexDirection: 'column', gap: '1rem' }}>
              {bookmarks.map((repo) => (
                <Link
                  key={repo.id}
                  to={`/repositories/${repo.slug}`}
                  style={{
                    display: 'block',
                    padding: '1rem',
                    background: 'var(--bg-tertiary)',
                    border: '1px solid var(--border)',
                    borderRadius: '8px',
                    textDecoration: 'none',
                    color: 'var(--text-primary)',
                    transition: 'all 0.2s ease',
                  }}
                  onMouseEnter={(e) => {
                    e.currentTarget.style.borderColor = 'var(--accent)'
                    e.currentTarget.style.transform = 'translateY(-1px)'
                  }}
                  onMouseLeave={(e) => {
                    e.currentTarget.style.borderColor = 'var(--border)'
                    e.currentTarget.style.transform = 'translateY(0)'
                  }}
                >
                  <div style={{
                    fontFamily: 'var(--font-mono)',
                    fontSize: '1rem',
                    fontWeight: '600',
                    color: 'var(--text-primary)',
                    marginBottom: '0.5rem',
                  }}>
                    {repo.title}
                  </div>
                  {repo.description && (
                    <p style={{
                      fontSize: '0.85rem',
                      color: 'var(--text-secondary)',
                      margin: '0.5rem 0',
                      lineHeight: 1.5,
                    }}>
                      {repo.description}
                    </p>
                  )}
                  <div style={{
                    display: 'flex',
                    gap: '1rem',
                    marginTop: '0.75rem',
                    fontFamily: 'var(--font-mono)',
                    fontSize: '0.75rem',
                    color: 'var(--text-muted)',
                  }}>
                    {repo.manufacturer && <span>{repo.manufacturer}</span>}
                    {(() => {
                      const deviceTypeLabel = getDeviceTypeLabel(repo)
                      return deviceTypeLabel ? <span>{deviceTypeLabel}</span> : null
                    })()}
                    {repo.version_count !== undefined && <span>{repo.version_count} versions</span>}
                    {repo.owner && <span>by {repo.owner.username}<CertifiedBadge user={repo.owner} /></span>}
                  </div>
                </Link>
              ))}
            </div>
          )}
        </div>
      </div>

      <div className="dashboard-featured">
        <FeaturedRepositories />
      </div>

      <ManageRepositoryModal
        repo={managingRepo}
        currentUserId={user?.id}
        onClose={() => setManagingRepo(null)}
        onDeleted={(slug) => {
          setMyRepos((current) => current.filter((r) => r.slug !== slug))
          setManagingRepo(null)
        }}
        onSelfRemoved={(slug) => {
          setMyRepos((current) => current.filter((r) => r.slug !== slug))
          setManagingRepo(null)
        }}
      />

      {/* Reject Modal */}
      {showRejectModal && selectedContribution && (
        <div style={{
          position: 'fixed',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          backgroundColor: 'rgba(0, 0, 0, 0.5)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          zIndex: 1000
        }}>
          <div style={{
            backgroundColor: 'var(--bg-primary)',
            padding: '2rem',
            borderRadius: '8px',
            maxWidth: '500px',
            width: '90%',
            boxShadow: '0 4px 6px rgba(0, 0, 0, 0.1)'
          }}>
            <h2 style={{ marginBottom: '1rem' }}>Reject Contribution</h2>
            <p style={{ marginBottom: '1rem', color: 'var(--text-muted)' }}>
              Please provide a reason for rejecting this contribution. The contributor will see this reason.
            </p>
            <textarea
              value={rejectReason}
              onChange={(e) => setRejectReason(e.target.value)}
              placeholder="Enter rejection reason..."
              style={{
                width: '100%',
                minHeight: '120px',
                padding: '0.75rem',
                border: '1px solid var(--border-color)',
                borderRadius: '4px',
                fontSize: '0.9rem',
                fontFamily: 'inherit',
                marginBottom: '1rem',
                resize: 'vertical'
              }}
            />
            <div style={{ display: 'flex', gap: '0.75rem', justifyContent: 'flex-end' }}>
              <button
                className="btn btn-secondary"
                onClick={() => {
                  setShowRejectModal(false)
                  setSelectedContribution(null)
                  setRejectReason('')
                }}
                disabled={isProcessing}
              >
                Cancel
              </button>
              <button
                className="btn btn-secondary"
                onClick={handleRejectSubmit}
                disabled={isProcessing || !rejectReason.trim()}
                style={{ backgroundColor: '#dc3545', color: 'white', borderColor: '#dc3545' }}
              >
                {isProcessing ? 'Rejecting...' : 'Reject'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  )
}

export default ContributorDashboard

